import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { PermissionStore } from '@/pages/service/core/permission-store';

/**
 * Shows a "view-only" strip at the top of any screen the current role can read
 * but not write. The actual write-blocking is enforced server-side (403); this
 * is the visible signal so users know edits won't save before they try.
 */
@Component({
    selector: 'app-screen-readonly-banner',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="ro-bar" *ngIf="readonly">
        <i class="pi pi-eye"></i>
        <span>View-only — your access allows seeing this screen but not editing it. Action buttons are hidden and fields are disabled.</span>
    </div>
    `,
    styles: [`
        .ro-bar { display:flex; align-items:center; gap:9px; padding:8px 14px; margin:0 0 14px; border-radius:var(--gh-r-sm); font-size:var(--gh-fs-13);
                  background:rgba(100,116,139,.1); border:1px solid rgba(100,116,139,.32); color:var(--gh-text-2); }
        .ro-bar i { font-size:var(--gh-fs-15); flex:0 0 auto; }
    `]
})
export class AppScreenReadonlyBanner implements OnInit, OnDestroy {
    readonly = false;
    private sub = new Subscription();

    constructor(private perms: PermissionStore, private router: Router) {}

    ngOnInit(): void {
        this.evaluate(this.router.url);
        this.sub.add(this.router.events.pipe(filter(e => e instanceof NavigationEnd))
            .subscribe((e: any) => this.evaluate((e as NavigationEnd).urlAfterRedirects)));
        this.sub.add(this.perms.changed$.subscribe(() => this.evaluate(this.router.url)));
    }

    ngOnDestroy(): void { this.sub.unsubscribe(); }

    private evaluate(url: string): void {
        // Single source of truth — the same check that drives the global
        // .screen-readonly mode (hidden buttons + disabled fields).
        this.readonly = this.perms.isViewOnlyUrl(url);
    }
}
